/**
 * Seeded fixed-step particle pool (art §4.14): advances only on its 1/120 s grid of vfx time, keeps prev/curr per
 * particle and is drawn at α — deterministic under any cadence (architecture §5.3 rule 5). Pure TypeScript (no DOM, no
 * GPU): the renderer copies `writeInstances()` into the instance buffer. P10's VFX director drives it through
 * `renderer.vfx.burst`.
 */
import type { ElementInk, ParticleKind } from '../../shared';
import { PIGMENT_LINEAR } from '../shaders/pigments';

export const STEP_MS = 1000 / 120;

/** Shape ids in the particle fragment. */
export const SHAPE = { splat: 0, sparkle: 1, tuft: 2, dust: 3, rubric: 4, clang: 5, blot: 6, drop: 7, slashArc:8,impactRing:9,parrySeal:10,cutLine:11,impactRay:12,sigilSeal:13 } as const;

const ELEMENT_RGB: Record<ElementInk, readonly [number, number, number]> = {
  ink: PIGMENT_LINEAR.ink, vermilion: PIGMENT_LINEAR.vermilion, gold: PIGMENT_LINEAR.gold, verdigris: PIGMENT_LINEAR.verdigris,
  lapis: PIGMENT_LINEAR.azurite, minium: PIGMENT_LINEAR.minium, folium: PIGMENT_LINEAR.folium,
};

/** mulberry32 — presentation-only randomness (never gameplay). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface BurstOpts { seed: number; dir?: [number, number, number]; count?: number; element?: ElementInk; color?: [number, number, number]; scale?: number; rotation?:number;lifeMs?:number }

export class ParticlePool {
  readonly cap: number;
  count = 0;
  private readonly px: Float32Array; private readonly py: Float32Array; private readonly pz: Float32Array;
  private readonly qx: Float32Array; private readonly qy: Float32Array; private readonly qz: Float32Array;
  private readonly vx: Float32Array; private readonly vy: Float32Array; private readonly vz: Float32Array;
  private readonly age: Float32Array; private readonly life: Float32Array; private readonly size: Float32Array;
  private readonly rot: Float32Array; private readonly spin: Float32Array; private readonly seed: Float32Array;
  private readonly shape: Uint8Array; private readonly grav: Float32Array; private readonly drag: Float32Array;
  private readonly r: Float32Array; private readonly g: Float32Array; private readonly b: Float32Array;
  private readonly grow: Float32Array; private readonly kind: Uint8Array;
  /** vfx time of the last grid step. */
  private stepMs = 0;
  private started = false;

  constructor(cap: number) {
    this.cap = cap;
    const f = (): Float32Array => new Float32Array(cap);
    this.px = f(); this.py = f(); this.pz = f(); this.qx = f(); this.qy = f(); this.qz = f();
    this.vx = f(); this.vy = f(); this.vz = f(); this.age = f(); this.life = f(); this.size = f();
    this.rot = f(); this.spin = f(); this.seed = f(); this.grav = f(); this.drag = f();
    this.r = f(); this.g = f(); this.b = f(); this.grow = f();
    this.shape = new Uint8Array(cap); this.kind = new Uint8Array(cap);
  }

  clear(): void { this.count = 0; }

  flareCuts():void {
    for(let i=0;i<this.count;i++)if(this.shape[i]===SHAPE.cutLine){
      this.life[i]=this.age[i]+.22;
      this.r[i]=1.35;this.g[i]=1.4;this.b[i]=1.48;
    }
  }

  private add(): number {
    if (this.count >= this.cap) return -1;
    return this.count++;
  }

  /** Spawns a burst at `at` (world, H). */
  burst(kind: ParticleKind, at: readonly [number, number, number], o: BurstOpts): void {
    const R = rng(o.seed * 2654435761 + kind.length * 97);
    const dir = o.dir ?? [1, 0.25, 0];
    const dl = Math.hypot(dir[0], dir[1], dir[2]) || 1;
    const dx = dir[0] / dl, dy = dir[1] / dl, dz = dir[2] / dl;
    const el = ELEMENT_RGB[o.element ?? (kind === 'sparkle' || kind === 'glint' ? 'gold' : 'ink')];
    const sc = o.scale ?? 1;
    const n = o.count ?? defaultCount(kind);
    for (let k = 0; k < n; k++) {
      const i = this.add();
      if (i < 0) return;
      let speed = 0, cone = 0, life = 0.6, size = 0.03, grav = 0, drag = 0, shape: number = SHAPE.splat, grow = 0, spin = 0;
      let col: readonly [number, number, number] = o.color ?? el;
      switch (kind) {
        case 'splat': case 'smite':
          { const u = R(); speed = 2.5 + u * 3.5; cone = 0.61; life = 0.6 + R() * 0.25; size = (0.026 - u * 0.016) * (0.7 + R() * 0.6); grav = 6; drag = 1.5; shape = SHAPE.splat; break; }
        case 'blot':
          speed = 0.4 + R() * 0.6; cone = 1.2; life = 1.5; size = 0.1 + R() * 0.15; grav = 0; drag = 2; shape = SHAPE.blot; grow = 1.2; col = PIGMENT_LINEAR.inkDeep; break;
        case 'sparkle': case 'glint':
          speed = 0.3 + R() * 0.6; cone = 3.1; life = 0.4 + R() * 0.5; size = 0.03 + R() * 0.035; grav = -0.3; drag = 1; shape = SHAPE.sparkle; spin = 1.57;
          col = [PIGMENT_LINEAR.glint[0] * 1.6, PIGMENT_LINEAR.glint[1] * 1.6, PIGMENT_LINEAR.glint[2] * 1.6]; break;
        case 'tuft':
          speed = 0.8 + R() * 1.2; cone = 1.0; life = 1.2; size = 0.04; grav = 0.6; drag = 2.5; shape = SHAPE.tuft; spin = 3; col = o.color ?? PIGMENT_LINEAR.ochre; break;
        case 'dust':
          speed = 0.2 + R() * 0.3; cone = 1.4; life = 0.45; size = 0.06 + R() * 0.03; grav = -0.2; drag = 3; shape = SHAPE.dust; grow = 1.4; col = PIGMENT_LINEAR.vellumAged; break;
        case 'rubric':
          speed = 0; life = 0.25; size = 0.3; shape = SHAPE.rubric; grow = 3; col = PIGMENT_LINEAR.vermilion; break;
        case 'clang':
          speed = 0; life = 0.18; size = 0.12; shape = SHAPE.clang; grow = 2; col = PIGMENT_LINEAR.leadWhite; break;
        case 'drip':
          speed = 0.2; cone = 0.3; life = 0.7; size = 0.03; grav = 3; drag = 1; shape = SHAPE.drop; col = PIGMENT_LINEAR.verdigris; break;
        case 'flame':
          speed = 0.8; cone = 0.5; life = 0.5; size = 0.08; grav = -0.8; drag = 1; shape = SHAPE.blot; col = PIGMENT_LINEAR.minium; break;
        case 'slashArc':
          speed=0;life=.2;size=.29;shape=SHAPE.slashArc;grow=.45;col=o.color??(o.element?el:PIGMENT_LINEAR.glint);break;
        case 'impactRing':
          speed=0;life=.5;size=.31;shape=SHAPE.impactRing;grow=3.6;col=o.color??(o.element?el:PIGMENT_LINEAR.gold);break;
        case 'parrySeal':
          speed=0;life=.32;size=.22;shape=SHAPE.parrySeal;grow=.35;col=o.color??PIGMENT_LINEAR.lapisPale;break;
        case 'cutLine':
          speed=0;life=.45;size=.42;shape=SHAPE.cutLine;grow=0;col=o.color??(o.element?el:PIGMENT_LINEAR.glint);break;
        case 'impactRay':
          speed=.5;cone=1.3;life=.22;size=.09;shape=SHAPE.impactRay;grav=.5;drag=2;col=o.color??(o.element?el:PIGMENT_LINEAR.glint);break;
        case 'sigilSeal':
          speed=0;life=.85;size=.4;shape=SHAPE.sigilSeal;col=o.color??PIGMENT_LINEAR.verdigris;break;
        default:
          speed = 1; cone = 1; life = 0.5; size = 0.04; shape = SHAPE.sparkle; break;
      }
      if(Number.isFinite(o.lifeMs))life=Math.max(.06,Math.min(5,o.lifeMs!/1000));
      // direction inside a cone around dir
      const a1 = (R() * 2 - 1) * cone, a2 = (R() * 2 - 1) * cone * 0.6;
      let ex = dx + a1 * -dy, ey = dy + a1 * dx, ez = dz + a2;
      const el2 = Math.hypot(ex, ey, ez) || 1; ex /= el2; ey /= el2; ez /= el2;
      const ox = (R() - 0.5) * 0.04 * sc, oy = (R() - 0.5) * 0.04 * sc;
      this.px[i] = this.qx[i] = at[0] + ox; this.py[i] = this.qy[i] = at[1] + oy; this.pz[i] = this.qz[i] = at[2] + 0.05;
      this.vx[i] = ex * speed * sc; this.vy[i] = ey * speed * sc; this.vz[i] = ez * speed * sc * 0.3;
      this.age[i] = 0; this.life[i] = life; this.size[i] = size * sc; this.rot[i] = R() * 6.283; this.spin[i] = spin * (R() - 0.5) * 2;
      if(shape===SHAPE.slashArc){this.rot[i]=Number.isFinite(o.rotation)?o.rotation!:Math.atan2(dy,dx);this.px[i]=this.qx[i]=at[0];this.py[i]=this.qy[i]=at[1];}
      if(shape===SHAPE.impactRing){this.rot[i]=0;this.px[i]=this.qx[i]=at[0];this.py[i]=this.qy[i]=at[1];this.pz[i]=this.qz[i]=at[2];}
      if(shape===SHAPE.parrySeal||shape===SHAPE.cutLine||shape===SHAPE.sigilSeal){this.rot[i]=o.rotation??0;this.px[i]=this.qx[i]=at[0];this.py[i]=this.qy[i]=at[1];this.pz[i]=this.qz[i]=at[2]+.015;}
      if(shape===SHAPE.impactRay)this.rot[i]=Math.atan2(ey,ex)+(R()-.5)*.8;
      this.seed[i] = R(); this.shape[i] = shape; this.grav[i] = grav * sc; this.drag[i] = drag; this.grow[i] = grow;
      this.r[i] = col[0]; this.g[i] = col[1]; this.b[i] = col[2]; this.kind[i] = 0;
    }
  }

  /** Advances on the fixed grid up to vfxMs. */
  advanceTo(vfxMs: number): void {
    if (!this.started) { this.stepMs = vfxMs; this.started = true; return; }
    if (vfxMs < this.stepMs) { this.stepMs = vfxMs; return; }
    let steps = 0;
    while (this.stepMs + STEP_MS <= vfxMs && steps < 240) {
      this.step(STEP_MS / 1000);
      this.stepMs += STEP_MS;
      steps++;
    }
    if (steps >= 240) this.stepMs = vfxMs;
  }

  private step(dt: number): void {
    let w = 0;
    for (let i = 0; i < this.count; i++) {
      const age = this.age[i] + dt;
      if (age >= this.life[i]) continue;
      this.qx[w] = this.px[i]; this.qy[w] = this.py[i]; this.qz[w] = this.pz[i];
      const dr = Math.max(0, 1 - this.drag[i] * dt);
      let vx = this.vx[i] * dr, vy = this.vy[i] * dr - this.grav[i] * dt; const vz = this.vz[i] * dr;
      let x = this.px[i] + vx * dt, y = this.py[i] + vy * dt; const z = this.pz[i] + vz * dt;
      if (y < 0.005 && this.grav[i] > 0) { y = 0.005; vx *= 0.2; vy = 0; }
      if (this.shape[i] === SHAPE.tuft) x += Math.sin(age * 6 + this.seed[i] * 20) * 0.15 * dt;
      this.px[w] = x; this.py[w] = y; this.pz[w] = z; this.vx[w] = vx; this.vy[w] = vy; this.vz[w] = vz;
      this.age[w] = age; this.life[w] = this.life[i]; this.size[w] = this.size[i]; this.rot[w] = this.rot[i] + this.spin[i] * dt;
      this.spin[w] = this.spin[i]; this.seed[w] = this.seed[i]; this.shape[w] = this.shape[i]; this.grav[w] = this.grav[i];
      this.drag[w] = this.drag[i]; this.grow[w] = this.grow[i]; this.r[w] = this.r[i]; this.g[w] = this.g[i]; this.b[w] = this.b[i];
      this.kind[w] = this.kind[i];
      w++;
    }
    this.count = w;
  }

  /** Writes instances (posSize, color, params) at α between prev and curr; returns the count. Zero allocation. */
  writeInstances(out: Float32Array, vfxMs: number, viewRight: readonly [number, number]): number {
    const alpha = Math.min(1, Math.max(0, (vfxMs - this.stepMs) / STEP_MS));
    const n = Math.min(this.count, Math.floor(out.length / 12));
    for (let i = 0; i < n; i++) {
      const o = i * 12;
      const x = this.qx[i] + (this.px[i] - this.qx[i]) * alpha;
      const y = this.qy[i] + (this.py[i] - this.qy[i]) * alpha;
      const z = this.qz[i] + (this.pz[i] - this.qz[i]) * alpha;
      const t = this.age[i] / this.life[i];
      let s = this.size[i];
      let a = 1;
      const sh = this.shape[i];
      if (sh === SHAPE.sparkle) { s *= Math.min(1, t / 0.08) * (t > 0.7 ? (1 - t) / 0.3 : 1); }
      else if(sh===SHAPE.slashArc){s*=.94+t*.18;a=Math.pow(1-t,1.5);}
      else if(sh===SHAPE.impactRing){s*=.35+t*2.2;a=Math.pow(1-t,1.3);}
      else if(sh===SHAPE.cutLine){a=Math.min(1,(this.life[i]-this.age[i])/.25);}
      else if(sh===SHAPE.impactRay){a=Math.pow(1-t,1.4);}
      // A seal stamps in, then holds its complete silhouette. No drift or
      // progressive half-lines; the final quarter fades as one bounded mark.
      else if(sh===SHAPE.sigilSeal){s*=.97+.03*Math.min(1,t/.09);a=Math.min(1,this.age[i]/.045)*Math.min(1,(1-t)/.25);}
      else if (this.grow[i] > 0) { s *= 0.6 + this.grow[i] * t * 0.6; a = 1 - Math.max(0, (t - 0.6) / 0.4); }
      else if (sh === SHAPE.tuft) a = t > 0.75 ? (1 - t) / 0.25 : 1;
      else a = t > 0.85 ? (1 - t) / 0.15 : 1;
      // splats stretch along their screen-space velocity
      const vx = this.vx[i], vy = this.vy[i];
      const speed = Math.hypot(vx, vy);
      let rot = this.rot[i], stretch = 1;
      if (sh === SHAPE.splat) { rot = Math.atan2(vy, vx * viewRight[0]); stretch = 1 + Math.min(1.6, speed * 0.22); }
      out[o] = x; out[o + 1] = y; out[o + 2] = z; out[o + 3] = s;
      out[o + 4] = this.r[i]; out[o + 5] = this.g[i]; out[o + 6] = this.b[i]; out[o + 7] = a * 0.95;
      out[o + 8] = rot; out[o + 9] = sh; out[o + 10] = stretch; out[o + 11] = sh===SHAPE.cutLine?Math.min(1,this.age[i]/.07):this.seed[i];
    }
    return n;
  }
}

/** Light-hit counts (art §4.14 table, first column). */
function defaultCount(kind: ParticleKind): number {
  switch (kind) {
    case 'splat': return 6;
    case 'sparkle': return 8;
    case 'tuft': return 2;
    case 'dust': return 3;
    case 'blot': return 3;
    default: return 1;
  }
}
